import { useLocalStorage } from "@elit-hair/elit-tools";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { PassengerTransferType } from "@/constants/passengerTransferTypes";
import { formatFlightNumber } from "@/helpers/strings";
import { WeTrackZoneDataWithGroup } from "@/pages/Map/components/MapZonesDrawer";
import useDriversContext from "@/providers/data/DriversContext";
import useAuth from "@/providers/hooks/useAuth";
import { ILocation } from "@/types/location.types";
import { OperationServiceFlightInfo } from "@/types/patient";
import {
    IPlanPassenger,
    PASSENGER_DEFAULT_STATUS_MILESTONES,
    RES_PassengerGroup,
    RES_Task,
    Task,
} from "@/types/tasks.types";
import { WeTrackVehicleData } from "@/types/wetrack.types";

export const useGetVehiclesQuery = (refetch: boolean) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    return useQuery<WeTrackVehicleData[]>({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_VEHICLES],
        refetchInterval: refetch ? 1000 * 3 : false,
        refetchOnWindowFocus: false,
        queryFn: () => axiosInstance.get("/map/vehicles/list").then(({ data }) => data?.features),
    });
};

export const useGetNewPassengers = ({ date, enabled = true }: { date: DateTime; enabled?: boolean }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();
    const isoTime = date.toISODate();
    const { parsedDrivers } = useDriversContext();
    return useQuery<IPlanPassenger<"db">[]>({
        enabled: enabled && Object.keys(parsedDrivers).length > 0 && !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_PASSENGERS, isoTime],
        refetchOnWindowFocus: false,
        queryFn: async () => {
            return await axiosInstance.get(`/passengers/plan/${isoTime}`).then(({ data }) => data);
        },
    });
};

export const useGetTasks = ({ date }: { date: DateTime }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    const { parsedDrivers } = useDriversContext();
    return useQuery<Task<IPlanPassenger<"api">>[]>({
        enabled: Object.keys(parsedDrivers).length > 0 && !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_TASKS, date.toISODate()],
        refetchOnWindowFocus: false,
        queryFn: () =>
            axiosInstance.get(`/tasks/list/${date.toISODate()}`).then((data) =>
                data.data.map(
                    (task: RES_Task): Task => ({
                        ...task,
                        pickUp: DateTime.fromISO(task.pickUp),
                        dropOff: DateTime.fromISO(task.dropOff),
                        driverName: parsedDrivers[task.driverId]?.name,
                        originalPickUp: DateTime.fromISO(task.originalPickUp),
                        originalDropOff: DateTime.fromISO(task.originalDropOff),
                        passengers: task.passengers.map((passenger) => ({
                            ...passenger,
                            pickUp: DateTime.fromISO(passenger.pickUp ? passenger.pickUp : ""),
                            dropOff: DateTime.fromISO(passenger.dropOff ? passenger.dropOff : ""),
                            flightTime: (passenger.flightTime && DateTime.fromISO(passenger.flightTime)) || null,
                        })),
                    })
                )
            ),
    });
};

export const useGetPatient = ({ id, raw }: { id: string | undefined; raw?: boolean }) => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useQuery<OperationServiceFlightInfo>({
        enabled: !!id,
        queryKey: [QUERY_KEYS.GET_PATIENT, id],
        queryFn: () =>
            axiosInstance
                .get(`/passengers/${id}?raw=${!!raw}`)
                .then(({ data }) => data)
                .catch((err) => {
                    Sentry.captureException(err);

                    enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                        variant: "error",
                    });
                    throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
                }),
    });
};

export const useGetLocationsQuery = () => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    const [locations, setLocations] = useLocalStorage<ILocation[]>("locations", []);

    return useQuery<ILocation[]>({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_LOCATIONS],
        initialData: locations,
        queryFn: () =>
            axiosInstance.get("/constants/locations").then(({ data }) => {
                setLocations(data);

                return data;
            }),
    });
};

export const useGetMapZones = () => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    return useQuery<WeTrackZoneDataWithGroup[]>({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_MAP_ZONES],
        queryFn: () => axiosInstance.get("/map/zones").then(({ data }) => data || []),
    });
};

export const useGetDriverCameras = (id: string) => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useQuery({
        enabled: !!id,
        queryKey: [QUERY_KEYS.GET_DRIVER_CAMERAS, id],
        queryFn: () => axiosInstance.get(`drivers/cameras/${id}`).then(({ data }) => data),
    });
};

export const useGetArrivalPassengers = ({ date, enabled }: { date: DateTime; enabled: boolean }) => {
    const axiosInstance = useGetTransportAxiosInstance();
    const key = date.startOf("day").toISO();

    return useQuery<IPlanPassenger<"api">[]>({
        enabled,
        queryKey: [QUERY_KEYS.GET_ARRIVALS, key],
        queryFn: () =>
            axiosInstance.get<RES_PassengerGroup[]>(`/passengers/arrivals/${date.toISODate()}`).then(({ data }) => {
                return data.map((passenger) => {
                    return {
                        ...passenger,
                        pickUp: passenger.pickUp ? DateTime.fromISO(passenger.pickUp) : null,
                        dropOff: passenger.dropOff ? DateTime.fromISO(passenger.dropOff) : null,
                        flightTime: passenger.flightTime ? DateTime.fromISO(passenger.flightTime) : null,
                        flightNumber: formatFlightNumber(passenger.flightNumber),
                        airport: "",
                        fullCompanions: [],
                        location: "",
                        driverId: "",
                        uetdsId: "",
                        uetdsPdfUrl: "",
                        extras: 0,
                        taskId: "",
                        transferType: PassengerTransferType.STANDARD,
                        hotelName: "",
                        statusMilestones: PASSENGER_DEFAULT_STATUS_MILESTONES,
                        people: passenger.people || 1,
                    } as IPlanPassenger<"api">;
                });
            }),
    });
};
